import classNames from "classnames";
import React, { useCallback, useLayoutEffect, useRef, useState } from "react";

import "./ACMiddleEllipsis.less";

const ELLIPSIS = "\u2026";
const MIN_CHARS_FOR_TRUNCATION = 4;

interface ACMiddleEllipsisProps {
  /** The full text to potentially truncate */
  text: string;
  /** Custom tooltip text (defaults to full text) */
  title?: string;
  /** Additional CSS class name */
  className?: string;
  /** When true, sets an inline max-width equal to the full text's measured width so the element does not grow beyond its content in flex layouts (default: false) */
  applyWidth?: boolean;
}

// ---------------------------------------------------------------------------
// Shared ResizeObserver — one observer for ALL ACMiddleEllipsis instances.
// Each element registers a callback via WeakMap. The observer batches
// notifications, so hundreds of instances only trigger one observer callback.
// ---------------------------------------------------------------------------
type ResizeCallback = () => void;
const resizeCallbacks = new WeakMap<Element, ResizeCallback>();
let sharedObserver: ResizeObserver | null = null;

function getSharedObserver(): ResizeObserver {
  if (!sharedObserver) {
    sharedObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const cb = resizeCallbacks.get(entry.target);
        if (cb) cb();
      }
    });
  }
  return sharedObserver;
}

function observeElement(el: Element, callback: ResizeCallback) {
  resizeCallbacks.set(el, callback);
  getSharedObserver().observe(el);
}

function unobserveElement(el: Element) {
  resizeCallbacks.delete(el);
  getSharedObserver().unobserve(el);
}

// ---------------------------------------------------------------------------
// DOM-based text measurement — uses a hidden span that inherits all CSS
// (font, letter-spacing, font-feature-settings, etc.) from the source
// element, so measurements are pixel-perfect.
// ---------------------------------------------------------------------------
let measureSpan: HTMLSpanElement | null = null;

function getTextWidth(text: string, sourceEl: Element): number {
  if (!measureSpan) {
    measureSpan = document.createElement("span");
    measureSpan.style.cssText =
      "position:absolute;visibility:hidden;white-space:nowrap;pointer-events:none;";
    document.body.appendChild(measureSpan);
  }
  // Copy all font-related styles from the source element so the
  // measurement accounts for letter-spacing, font-features, etc.
  const style = getComputedStyle(sourceEl);
  measureSpan.style.fontWeight = style.fontWeight;
  measureSpan.style.letterSpacing = style.letterSpacing;
  measureSpan.style.wordSpacing = style.wordSpacing;
  measureSpan.style.fontStyle = style.fontStyle;
  measureSpan.style.fontSize = style.fontSize;
  measureSpan.style.fontFamily = style.fontFamily;
  measureSpan.textContent = text;
  return measureSpan.getBoundingClientRect().width;
}

function findTruncatedText(
  text: string,
  containerWidth: number,
  sourceEl: Element,
): string {
  if (text.length < MIN_CHARS_FOR_TRUNCATION) {
    return text;
  }

  let low = MIN_CHARS_FOR_TRUNCATION;
  let high = text.length - 1;
  let bestResult = text.charAt(0) + ELLIPSIS + text.charAt(text.length - 1);

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    const startCount = Math.ceil(mid / 2);
    const endCount = Math.floor(mid / 2);

    const candidate =
      endCount > 0
        ? text.slice(0, startCount) + ELLIPSIS + text.slice(-endCount)
        : text.slice(0, startCount) + ELLIPSIS;

    const candidateWidth = getTextWidth(candidate, sourceEl);

    if (candidateWidth <= containerWidth) {
      bestResult = candidate;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  return bestResult;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function ACMiddleEllipsis({
  text,
  title,
  className,
  applyWidth = false,
}: ACMiddleEllipsisProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [displayText, setDisplayText] = useState(text);
  const [isTruncated, setIsTruncated] = useState(false);
  // Inline max-width caps flex growth at the full text's natural width
  // so short names don't stretch to fill the row.
  const [maxWidth, setMaxWidth] = useState<number | undefined>(undefined);
  const rafRef = useRef(0);

  const recompute = useCallback(() => {
    cancelAnimationFrame(rafRef.current);

    rafRef.current = requestAnimationFrame(() => {
      const container = containerRef.current;
      if (!container) return;

      const containerWidth = Math.round(container.clientWidth);
      if (containerWidth <= 0) return;

      const fullTextWidth = Math.round(getTextWidth(text, container));

      // Cap the element so it never grows beyond the full text width
      if (applyWidth) {
        setMaxWidth(fullTextWidth);
      } else {
        setMaxWidth(undefined);
      }

      if (fullTextWidth <= containerWidth) {
        setDisplayText(text);
        setIsTruncated(false);
        return;
      }

      const truncated = findTruncatedText(text, containerWidth, container);
      setDisplayText(truncated);
      setIsTruncated(truncated !== text);
    });
  }, [text, applyWidth]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const fullTextWidth = Math.round(getTextWidth(text, container));

    // Set max-width immediately so the flex item is capped from the start
    if (applyWidth) {
      setMaxWidth(fullTextWidth);
    } else {
      setMaxWidth(undefined);
    }

    // Initial computation — run synchronously for first paint
    const containerWidth = Math.round(container.clientWidth);
    if (containerWidth > 0) {
      if (fullTextWidth > containerWidth) {
        const truncated = findTruncatedText(text, containerWidth, container);
        setDisplayText(truncated);
        setIsTruncated(truncated !== text);
      } else {
        setDisplayText(text);
        setIsTruncated(false);
      }
    }

    // Observe the container itself — with flex: 1 1 0 it will resize
    // when siblings appear/disappear, triggering recompute.
    observeElement(container, recompute);
    return () => {
      cancelAnimationFrame(rafRef.current);
      unobserveElement(container);
    };
  }, [text, recompute, applyWidth]);

  if (!text) return null;

  const nativeTitle = isTruncated ? title || text : undefined;

  return (
    <div
      ref={containerRef}
      title={nativeTitle}
      className={classNames("ac-middle-ellipsis", className)}
      style={maxWidth !== undefined ? { maxWidth } : undefined}
    >
      <span className="ac-middle-ellipsis__text">{displayText}</span>
    </div>
  );
}
